import { useState } from 'react';
import { ChevronDown, ChevronUp, Zap, Shield, MessageSquare, Users, Server, Layout, Layers, Globe, Gauge, Puzzle, Sparkles, ArrowRight, X } from 'lucide-react';
import LeadCaptureModal from '../../../components/LeadCaptureModal';

export default function QuoraAIVersion_1_0() {
  const [openFaq, setOpenFaq] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [modalState, setModalState] = useState({
    isOpen: false,
    source: 'sdk',
    buttonType: 'orcamento'
  });

  const openModal = (buttonType = 'orcamento') => {
    setModalState({
      isOpen: true,
      source: 'sdk',
      buttonType
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const components = [
    {
      icon: <Users className="w-8 h-20" />,
      title: "Agentes Ilimitados",
      description: "Crie e gerencie quantos agentes precisar sem restrições",
      fullDescription: "Sistema completo de gerenciamento de agentes de IA sem limitações. Crie, configure e implante múltiplos agentes simultaneamente, cada um com suas próprias configurações, personalidade e conjunto de ferramentas. Ideal para empresas que precisam escalar suas operações de IA rapidamente.",
      gradient: "from-black-500 to-pink-500"
    },
    {
      icon: <MessageSquare className="w-8 h-20" />,
      title: "Controle de Conversas",
      description: "Pause, depure e monitore cada interação em tempo real",
      fullDescription: "Controle total sobre todas as conversas entre agentes e usuários. Funcionalidades incluem: pausar conversas em andamento, modo debug com visualização de chamadas de ferramentas, histórico completo de mensagens, e análise do raciocínio do agente em tempo real.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Gauge className="w-8 h-20" />,
      title: "Otimização de Custos",
      description: "Monitore tokens e otimize gastos automaticamente",
      fullDescription: "Dashboard completo de monitoramento de custos com análise detalhada de uso de tokens por agente, conversa e período. Configurações de limites de gastos, alertas automáticos e relatórios detalhados para controle total do orçamento.",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: <Puzzle className="w-8 h-20" />,
      title: "Integração MCP & HTTP",
      description: "Conecte ferramentas externas com facilidade",
      fullDescription: "Suporte nativo para Model Context Protocol (MCP) e integrações HTTP. Cadastre novas ferramentas facilmente, configure webhooks para eventos específicos, e expanda as capacidades dos seus agentes com serviços externos.",
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: <Shield className="w-8 h-20" />,
      title: "Segurança Avançada",
      description: "JWT, criptografia e autenticação robusta",
      fullDescription: "Segurança enterprise com autenticação JWT, criptografia de ponta a ponta, controle de acesso baseado em roles (RBAC), logs de auditoria completos e compliance com padrões de segurança internacionais.",
      gradient: "from-indigo-500 to-black-500"
    },
    {
      icon: <Globe className="w-8 h-20" />,
      title: "Multi-Provedores IA",
      description: "OpenAI e Azure OpenAI nativamente suportados",
      fullDescription: "Flexibilidade total para escolher e alternar entre provedores de IA. Suporte nativo para OpenAI e Azure OpenAI, com possibilidade de adicionar novos provedores. Balanceamento automático de carga entre provedores.",
      gradient: "from-pink-500 to-rose-500"
    }
  ];

  const faqs = [
    {
      question: "Como funciona a instalação do SDK?",
      answer: "Oferecemos suporte completo para instalação nos seus servidores. O SDK utiliza containers Docker para englobar os serviços de backend (FastAPI) e frontend (React), facilitando a implantação em qualquer ambiente."
    },
    {
      question: "Qual tipo de suporte está incluído?",
      answer: "A licença inclui suporte para instalação inicial. Para manutenção contínua, correção de bugs e acesso a novas atualizações, oferecemos planos de assinatura específicos."
    },
    {
      question: "Posso customizar o SDK para meu negócio?",
      answer: "Sim! A licença permite edições e customizações completas do código para adequar às suas necessidades específicas. A única restrição é a comercialização do SDK modificado."
    },
    {
      question: "Quais são os requisitos técnicos?",
      answer: "O SDK é compatível com qualquer ambiente que suporte Docker, incluindo Google Cloud, AWS, Azure e servidores on-premise. Recomendamos conhecimento intermediário a avançado em Python e React."
    }
  ];

  const cloudProviders = [
    { name: "Google Cloud", icon: "https://avatars.githubusercontent.com/u/2810941?s=280&v=4", color: "from-blue-400 to-blue-600" },
    { name: "AWS", icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/Amazon_Web_Services_Logo.svg/1280px-Amazon_Web_Services_Logo.svg.png", color: "from-orange-400 to-orange-600" },
    { name: "Azure", icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Microsoft_Azure.svg/1200px-Microsoft_Azure.svg.png", color: "from-sky-400 to-sky-600" },
    { name: "On-Premise", icon: "https://www.svgrepo.com/show/375439/gke-on-prem.svg", color: "from-gray-400 to-gray-600" }
  ];

  const databases = [
    { name: "Azure Cosmos DB", icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/Azure_Cosmos_DB.svg/800px-Azure_Cosmos_DB.svg.png", color: "from-indigo-400 to-indigo-600" },
    { name: "MongoDB", icon: "https://cdn.worldvectorlogo.com/logos/mongodb-icon-1.svg", color: "from-green-400 to-green-600" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-black-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Component Modal */}
      {selectedComponent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedComponent(null)}>
          <div className="relative max-w-lg w-full bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-white/20 p-8" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedComponent(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className={`w-16 h-16 bg-gradient-to-r ${selectedComponent.gradient} rounded-xl flex items-center justify-center mb-6 text-white`}>
              {selectedComponent.icon}
            </div>
            
            <h3 className="text-2xl font-bold text-white mb-4">{selectedComponent.title}</h3>
            <p className="text-gray-300 leading-relaxed">{selectedComponent.fullDescription}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="relative bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-400">Distribuído por</span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Paware Softwares
              </span>
            </div>
            <button 
              onClick={() => window.location.href = '/'} 
              className="px-4 py-2 text-sm text-gray-300 hover:text-white border border-gray-600 hover:border-gray-400 rounded-lg transition-all duration-300 hover:bg-white/10"
            >
              ← Voltar ao Início
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative container mx-auto px-6 pt-20 pb-16">
        <div className="text-center max-w-5xl mx-auto">
          <div className="mb-6 inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500/20 to-black-500/20 rounded-full border border-blue-400/30">
            <Sparkles className="w-4 h-4 text-blue-400 mr-2" />
            <span className="text-sm text-blue-300">Tecnologia de Ponta para Agentes de IA</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-blue-200 to-cyan-200 bg-clip-text text-transparent">
            Quora SDK
          </h1>
          <div className="text-3xl font-light text-blue-300 mb-8">
            Versão 1.0
          </div>
          
          <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto">
            Revolucione a criação de agentes de IA com nossa solução completa. 
            <span className="text-blue-400 font-semibold"> Velocidade superior</span>, 
            <span className="text-cyan-400 font-semibold"> simplicidade incomparável</span> e 
            <span className="text-black-400 font-semibold"> tecnologia moderna</span>.
          </p>

          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <div className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full">
              <Zap className="w-5 h-5 text-yellow-400" />
              <span className="text-white">Deploy em Minutos</span>
            </div>
            <div className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="text-white">Segurança Enterprise</span>
            </div>
            <div className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full">
              <Layers className="w-5 h-5 text-black-400" />
              <span className="text-white">Stack Completa</span>
            </div>
          </div>

          <button 
            onClick={() => openModal('saiba_mais')}
            className="group relative px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg font-semibold text-white shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105"
          >
            <span className="flex items-center">
              Solicitar Demonstração
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        </div>
      </section>

      {/* Main SDK Components */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Arquitetura Completa do SDK
            </h2>
            <p className="text-lg md:text-xl text-gray-400">Dois componentes principais, integração perfeita</p>
          </div>

          <div className="flex flex-col lg:flex-row justify-center gap-8 max-w-4xl mx-auto">
            {/* Servidor Component */}
            <div className="group relative flex-1 lg:max-w-md">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-black-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-blue-900/90 to-black-900/90 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-white/20 hover:border-white/40 transition-all duration-300 h-full">
                <div className="flex items-start mb-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Server className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2">Servidor</h3>
                    <p className="text-blue-300 text-base md:text-lg mb-1">API SDK</p>
                    <p className="text-gray-300 text-xs md:text-sm">Docker • Deploy Simplificado</p>
                  </div>
                </div>

                <div className="mt-auto pt-6 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-xs md:text-sm text-gray-400">Escalabilidade</span>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse animation-delay-200"></div>
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse animation-delay-400"></div>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-4 right-4 w-16 h-16 rounded-xl flex items-center justify-center">
                </div>
              </div>
            </div>

            {/* Plataforma Component */}
            <div className="group relative flex-1 lg:max-w-md">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-cyan-900/90 to-blue-900/90 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-white/20 hover:border-white/40 transition-all duration-300 h-full">
                <div className="flex items-start mb-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Layout className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2">Plataforma</h3>
                    <p className="text-cyan-300 text-base md:text-lg mb-1">Interface SDK</p>
                    <p className="text-gray-300 text-xs md:text-sm">Docker • Deploy Simplificado</p>
                  </div>
                </div>

                <div className="mt-auto pt-6 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-xs md:text-sm text-gray-400">Interface Responsiva</span>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse animation-delay-200"></div>
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse animation-delay-400"></div>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-4 right-4 w-16 h-16 rounded-xl flex items-center justify-center">
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Components Grid */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Componentes Poderosos Incluídos
            </h2>
            <p className="text-xl text-gray-400">Clique em cada componente para mais detalhes</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 max-w-6xl mx-auto">
            {components.map((component, index) => (
              <div
                key={index}
                onClick={() => setSelectedComponent(component)}
                className="group relative cursor-pointer"
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${component.gradient} rounded-2xl blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-300`}></div>
                <div className="relative bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:border-white/30 w-40 h-40 flex flex-col items-center justify-center">
                  <div className={`w-20 h-20 bg-gradient-to-r ${component.gradient} rounded-xl flex items-center justify-center mb-3 text-white`}>
                    {component.icon}
                  </div>
                  <h3 className="text-ms font-semibold text-white text-center leading-tight">{component.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Infraestrutura Enterprise-Ready
            </h2>
            <p className="text-xl text-gray-400">Deploy onde sua empresa precisar</p>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Cloud Providers */}
            <div className="mb-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {cloudProviders.map((provider, index) => (
                  <div key={index} className="group relative">
                    <div className={`absolute inset-0 bg-gradient-to-r ${provider.color} rounded-xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity`}></div>
                    <div className="relative bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 text-center hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:border-white/30">
                      <img className="mb-3 h-10 mx-auto block" src={provider.icon}></img>
                      <h4 className="text-white font-semibold">{provider.name}</h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center mb-10 mt-20">
              <p className="text-xl text-gray-400">Bancos de Dados Suportados</p>
            </div>

            {/* Databases */}
            <div>
              <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                {databases.map((db, index) => (
                  <div key={index} className="group relative">
                    <div className={`absolute inset-0 bg-gradient-to-r ${db.color} rounded-xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity`}></div>
                    <div className="relative bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 text-center hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:border-white/30">
                      <img className="mb-3 h-20 mx-auto block" src={db.icon}></img>
                      <h4 className="text-white font-semibold">{db.name}</h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Perguntas Frequentes
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 transition-colors">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between"
                >
                  <span className="font-medium text-white">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-300">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="relative bg-black/50 backdrop-blur-xl border-t border-white/10 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-400">© 2025 Quora SDK v1.0</p>
              <p className="text-sm text-gray-500">
                Distribuído por <span className="text-blue-400 font-semibold">Paware Softwares</span>
              </p>
            </div>
            <div className="text-sm text-gray-400 text-center md:text-right">
              <p>Licença permite edição completa do código</p>
              <p className="text-xs text-gray-500 mt-1">Uso comercial do SDK modificado não permitido</p>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        .animation-delay-400 {
          animation-delay: 400ms;
        }
      `}</style>

      <LeadCaptureModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        source={modalState.source}
        buttonType={modalState.buttonType}
      />
    </div>
  );
}